import { useState } from 'react';
import { useNavigate } from 'react-router';
import { describeRepeats } from '@1qa/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, CircleAlert, FileText, Gauge, Info, KeyRound, Link2, Sparkles } from '@/components/icons';
import { api, errorMessage, type LoadScriptRow, type PerfImportResult } from '@/lib/api';
import { Badge, Button, Card, CardBody, CardHeader, Field, Input, useToast } from '@/components/ui';

const HOW: Record<string, string> = {
  diff: 'changes between the two recordings',
  search: 'found in an earlier response',
  rule: 'known token name',
  generator: 'made by the browser',
  ai: 'suggested by AI',
  user: 'set by you',
};

/**
 * Steps 2 and 3 of a new performance script, whatever the recording came from (the 1QA Recorder, a HAR file): what 1QA found (values that change per session, test data, values it could not explain, transactions),
 * then name it, set the pause between pages, and save it to the project's script library - optionally straight into a
 * new performance test (the load and goals are set there).
 */
export function ScriptReview({
  projectId,
  review,
  defaultName,
  secondHint,
  source,
  sourceTestId,
}: {
  projectId: string;
  review: PerfImportResult;
  defaultName: string;
  secondHint: string;
  source: LoadScriptRow['source'];
  sourceTestId?: string | null;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState(defaultName);
  const [pauseS, setPauseS] = useState(2);

  const save = useMutation({
    mutationFn: (_then: 'script' | 'test') => {
      const think = pauseS > 0 ? { min: Math.round(pauseS * 500), max: Math.round(pauseS * 1500) } : undefined;
      const script = { ...review.script, transactions: review.script.transactions.map(({ thinkTimeMs: _recorded, ...t }) => ({ ...t, ...(think ? { thinkTimeMs: think } : {}) })) };
      return api.createPerfScript(projectId, { name: name.trim(), script, source, sourceTestId: sourceTestId ?? null });
    },
    onSuccess: (s, then) => {
      qc.invalidateQueries({ queryKey: ['perf-scripts', projectId] });
      toast.success(then === 'test' ? 'Script saved. Now set the load of the performance test.' : 'Script saved to the project’s library');
      navigate(then === 'test' ? `/projects/${projectId}/perf/new?script=${encodeURIComponent(s.id)}` : `/projects/${projectId}/perf/scripts/${s.id}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const steps = review.script.transactions.reduce((n, t) => n + t.steps.length, 0);
  return (
    <>
      <Card>
        <CardHeader
          title="2. Check what 1QA found"
          description={`${steps} requests in ${review.script.transactions.length} transactions; ${review.droppedEntries} requests left out (images, styles, other sites).`}
        />
        <CardBody className="space-y-5 text-sm">
          {review.problems.map((p) => (
            <p key={p} role="alert" className="flex items-start gap-2 rounded-field bg-danger-subtle p-3 text-fg ring-1 ring-danger-line ring-inset">
              <CircleAlert className="mt-0.5 size-4 shrink-0 text-danger-text" /> {p}
            </p>
          ))}
          {review.problems.some((p) => p.includes('load targets')) && (
            <p className="text-fg-secondary">
              Your app’s API runs on another address. Once a Project Admin adds it under{' '}
              <a href={`/projects/${projectId}?tab=settings`} className="text-link hover:underline">
                Settings › Load limits › Other hosts tests may load
              </a>
              , come back to this page and save the script (the recording is kept).
            </p>
          )}
          {!!review.frontEndLeftOut && (
            <p className="text-fg-secondary">
              {review.frontEndLeftOut} front-end request{review.frontEndLeftOut === 1 ? '' : 's'} (pages, framework requests, prefetches) left out: the script holds the backend calls
              only.
            </p>
          )}
          {describeRepeats(review.repeatsLeftOut) && <p className="text-fg-secondary">{describeRepeats(review.repeatsLeftOut)}</p>}
          {review.leftOut && review.leftOut.length > 0 && (
            <div className="rounded-field bg-surface-sunken p-3 ring-1 ring-border-subtle ring-inset">
              <p className="text-fg">Left out, as other companies’ sites (analytics, chat, maps…):</p>
              <ul className="mt-1 font-mono text-xs text-fg-secondary">
                {review.leftOut.map((h) => (
                  <li key={h.host}>
                    {h.host}: {h.requests} request{h.requests === 1 ? '' : 's'} ({h.methods.join(', ')})
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-fg-muted">If one of them is your own API, a Project Admin can add it to the project’s load limits; record the journey again after that.</p>
            </div>
          )}
          {review.recordedErrors.length > 0 && (
            <div role="alert" className="flex items-start gap-2 rounded-field bg-warning-subtle p-3 text-fg ring-1 ring-warning/40 ring-inset">
              <CircleAlert className="mt-0.5 size-4 shrink-0 text-warning" />
              <div>
                <p>
                  The app answered {review.recordedErrors.length === 1 ? 'this request' : `${review.recordedErrors.length} requests`} with an error while it was recorded, so the load test would expect
                  the same error:
                </p>
                <ul className="mt-1 font-mono text-xs">
                  {review.recordedErrors.slice(0, 5).map((e) => (
                    <li key={e.stepId}>
                      {e.name} → {e.status}
                    </li>
                  ))}
                </ul>
                <p className="mt-1 text-xs text-fg-secondary">If that is not how the journey should go (a failed sign-in, say), add a check to the test, fix it and record it again.</p>
              </div>
            </div>
          )}
          {review.missingData && review.missingData.length > 0 && (
            <p role="status" className="flex items-start gap-2 rounded-field bg-warning-subtle p-3 text-fg ring-1 ring-warning/40 ring-inset">
              <CircleAlert className="mt-0.5 size-4 shrink-0 text-warning" />
              <span>
                The script uses test data this performance project does not have yet: {review.missingData.join(', ')}. Add{' '}
                {review.missingData.length === 1 ? 'it' : 'them'} on the project’s{' '}
                <a href={`/projects/${projectId}?tab=data`} className="text-link hover:underline">
                  Test data
                </a>{' '}
                page before the first run.
              </span>
            </p>
          )}
          {review.missingFiles && review.missingFiles.length > 0 && (
            <p role="status" className="flex items-start gap-2 rounded-field bg-warning-subtle p-3 text-fg ring-1 ring-warning/40 ring-inset">
              <CircleAlert className="mt-0.5 size-4 shrink-0 text-warning" />
              <span>
                The journey uploads {review.missingFiles.filter((f) => f !== '(not chosen)').join(', ') || 'a file'}. Browsers don’t record the files themselves: upload{' '}
                {review.missingFiles.length === 1 ? 'it' : 'them'} under{' '}
                <a href={`/projects/${projectId}?tab=scripts`} className="text-link hover:underline">
                  Scripts › Test files
                </a>{' '}
                before the first run{review.missingFiles.includes('(not chosen)') ? ', and choose the file for each upload field in the script editor' : ''}.
              </span>
            </p>
          )}
          {review.note && (
            <p role="status" className="flex items-start gap-2 rounded-field bg-warning-subtle p-3 text-fg ring-1 ring-warning/40 ring-inset">
              <CircleAlert className="mt-0.5 size-4 shrink-0 text-warning" /> {review.note}
            </p>
          )}
          <section>
            <h3 className="mb-2 flex items-center gap-2 font-semibold text-fg">
              <Sparkles className="size-4 text-primary" aria-hidden /> Values that change per session ({review.correlations.length})
            </h3>
            {review.correlations.length === 0 ? (
              <p className="text-fg-secondary">None found: every request can be sent as recorded.</p>
            ) : (
              <ul className="divide-y divide-border-subtle rounded-card ring-1 ring-border-subtle ring-inset">
                {review.correlations.map((c) => (
                  <li key={c.var} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5">
                    <code className="font-mono text-xs font-semibold text-fg">{c.var}</code>
                    <span className="font-mono text-xs text-fg-muted">{c.preview}</span>
                    <span className="text-xs text-fg-secondary">{c.kind === 'extract' ? `taken from ${c.source?.where ?? 'a response'}` : 'made new for every user'}</span>
                    <span className="ml-auto flex items-center gap-2">
                      <span className="text-xs text-fg-muted">
                        {HOW[c.origin] ?? c.origin} · used {c.usedIn}×
                      </span>
                      {c.confirmed ? <Badge tone="success">Confirmed</Badge> : <Badge tone="warning">Not confirmed</Badge>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {review.recordings === 1 && !review.note && review.correlations.length > 0 && (
              <p className="mt-2 flex items-start gap-2 text-xs text-fg-muted">
                <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden /> {secondHint}
              </p>
            )}
          </section>
          {review.parameters.length > 0 && (
            <section>
              <h3 className="mb-2 flex items-center gap-2 font-semibold text-fg">
                <KeyRound className="size-4 text-primary" aria-hidden /> Test data used
              </h3>
              <div className="flex flex-wrap gap-2">
                {review.parameters.map((p) => (
                  <Badge key={p.key} tone={p.secret ? 'warning' : 'neutral'}>
                    {p.key}
                    {p.secret ? ' (secret)' : ''} · {p.usedIn}×
                  </Badge>
                ))}
              </div>
              <p className="mt-2 text-xs text-fg-muted">Values typed that match this project’s test data are sent from the test data of each run (secrets stay secret).</p>
            </section>
          )}
          {review.unresolved.length > 0 && (
            <section>
              <h3 className="mb-2 flex items-center gap-2 font-semibold text-warning">
                <CircleAlert className="size-4" aria-hidden /> Values 1QA could not explain ({review.unresolved.length})
              </h3>
              <ul className="space-y-1 text-xs text-fg-secondary">
                {review.unresolved.map((u, i) => (
                  <li key={i}>
                    <code className="font-mono">{u.slot}</code> in step {u.stepId} ({u.value}): {u.reason}. The check run will show whether the app accepts it.
                  </li>
                ))}
              </ul>
            </section>
          )}
          <section>
            <h3 className="mb-2 flex items-center gap-2 font-semibold text-fg">
              <FileText className="size-4 text-primary" aria-hidden /> Transactions
            </h3>
            <ol className="space-y-2">
              {review.script.transactions.map((t) => (
                <li key={t.id} className="rounded-card px-4 py-2 ring-1 ring-border-subtle ring-inset">
                  <div className="font-medium text-fg">{t.name}</div>
                  <ul className="mt-1 space-y-0.5">
                    {t.steps.map((s) => (
                      <li key={s.id} className="flex items-center gap-2 font-mono text-xs break-all text-fg-secondary">
                        {s.name}
                        {s.kind === 'http' && s.extract.length > 0 && (
                          <span className="inline-flex items-center gap-1 text-primary" title={s.extract.map((x) => x.var).join(', ')}>
                            <Link2 className="size-3" aria-hidden /> {s.extract.map((x) => x.var).join(', ')}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ol>
          </section>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="3. Save the script" description="Scripts go to the project’s library; performance tests use them in their scenarios, with their own load and goals." />
        <CardBody className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-[2fr_1fr]">
            <Field label="Name">{(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />}</Field>
            <Field label="Pause between pages (s)" hint="Like a person reading; scenarios can change it">
              {(id) => <Input id={id} type="number" min={0} step={0.5} value={pauseS} onChange={(e) => setPauseS(Number(e.target.value))} />}
            </Field>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3">
            {review.problems.length > 0 && <span className="text-xs text-danger-text">Fix the problems above first.</span>}
            <Button variant="secondary" onClick={() => save.mutate('script')} disabled={!name.trim() || review.problems.length > 0} loading={save.isPending && save.variables === 'script'} icon={<CheckCircle2 className="size-4" />}>
              Save script
            </Button>
            <Button onClick={() => save.mutate('test')} disabled={!name.trim() || review.problems.length > 0} loading={save.isPending && save.variables === 'test'} icon={<Gauge className="size-4" />}>
              Save and make a performance test
            </Button>
          </div>
        </CardBody>
      </Card>
    </>
  );
}
